import React, { memo, useMemo, useCallback } from 'react';
import { ReminderFormType } from './EditMyScheduleModal';
import { FormSelect, FormInput } from '@core/components/form';
import { Button, Col, Row } from 'reactstrap';
import { formGroup, formControl } from '@core/utils/form';
import { useNonNilObservable } from '@core/utils/hooks/rxjs';
import { useDidMount } from '@core/utils/hooks/react';

export interface ReminderItemProps {
  form: ReminderFormType;
  onRemove: () => void;
}

function defineDurationForm() {
  return formGroup({
    value: formControl<number>({
      initialValue: 30,
      required: true,
    }),
    type: formControl<string>({
      initialValue: 'minutes',
      required: true,
    }),
  });
}

export const ReminderItem = memo((props: ReminderItemProps) => {
  const { form, onRemove } = props;
  const formValue = useNonNilObservable(form.value$);
  const inForm = useMemo(() => defineDurationForm(), []);
  const inFormValue = useNonNilObservable(inForm.value$);

  useDidMount(() => {
    if (formValue.time_before_event < 60) {
      inForm.patchValue({ value: formValue.time_before_event, type: 'minutes' });
    } else if (formValue.time_before_event < 1440) {
      inForm.patchValue({ value: formValue.time_before_event / 60, type: 'hours' });
    } else {
      inForm.patchValue({ value: formValue.time_before_event / 1440, type: 'days' });
    }
  });

  const setTime = useCallback(
    (type: string, inValue: number) => {
      let value = inValue;
      if (type == 'hours') {
        value = value * 60;
      } else if (type == 'days') {
        value = value * 60 * 24;
      }
      form.controls.time_before_event.setValue(value);
    },
    [form.controls.time_before_event],
  );

  const durationValues = useMemo(() => {
    if (inFormValue.type == 'minutes') {
      return [15, 30, 45].map(value => ({ label: value.toString(), value }));
    } else if (inFormValue.type == 'hours') {
      return [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20, 21, 22, 23].map(value => ({ label: value.toString(), value }));
    } else {
      return [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20, 21, 22, 23, 24, 25, 26, 27, 28, 29, 30, 31].map(value => ({
        label: value.toString(),
        value,
      }));
    }
  }, [inFormValue.type]);

  return (
    <div className="border-bottom pb-4 mb-4">
      <Row>
        <Col>
          <FormSelect
            control={form.controls.delivery_recipient}
            items={[
              { label: 'Event Guests', value: 'customer' },
              { label: 'Event Host', value: 'owner' },
              { label: 'Send to Both', value: 'both' },
            ]}
          />
        </Col>
        <Col>
          <FormInput control={form.controls.email_subject} />
        </Col>
      </Row>
      <Row>
        <Col className="d-flex">
          <div style={{ flex: 2, marginRight: 20 }}>
            <FormSelect control={inForm.controls.value} items={durationValues} onChange={value => setTime(inFormValue.type, value!)} />
          </div>
          <div style={{ flex: 5 }}>
            <FormSelect
              control={inForm.controls.type}
              onChange={value => {
                if (value == 'minutes') {
                  inForm.controls.value.setValue(15);
                  setTime('minutes', 15);
                } else if (value == 'hours') {
                  inForm.controls.value.setValue(1);
                  setTime('hours', 1);
                } else if (value == 'days') {
                  inForm.controls.value.setValue(1);
                  setTime('days', 1);
                }
              }}
              items={[
                { label: 'Minutes before the event', value: 'minutes' },
                { label: 'Hours before the event', value: 'hours' },
                { label: 'Days before the event', value: 'days' },
              ]}
            />
          </div>
        </Col>
        <Col></Col>
      </Row>
      <Button onClick={() => onRemove()} color="danger">
        <i className="fas fa-trash-alt "></i>
      </Button>
    </div>
  );
});
